import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, ComponentSync, LL_H_C, LL_V_L, ModuleFE_Dialog, TS_Checkbox, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { default_NoteTemplateSection } from '@app/advisor-content-shared';
import './Editor_NoteTemplateSections.scss';
import { Dialog_Confirmation, ICONS, viewMode_Edit, viewMode_View } from '@app/styles';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { DefaultEditor_InputText_Optional, DefaultEditor_TextArea } from '@app/styles/frontend/ui-manager/ui-components';
import { generateHex, swapInArrayByIndex } from '@nu-art/ts-common';
export class Editor_NoteTemplateSections extends ComponentSync {
    // ######################## Life Cycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editable = nextProps.editable;
        state.editMode = nextProps.editMode;
        state.indent = nextProps.indent;
        state.level = nextProps.level;
        return state;
    }
    // ######################## Logic ########################
    getViewMode = () => this.state.editMode ? viewMode_Edit : viewMode_View;
    moveSectionIndex = (sectionIndex, direction) => {
        const lastIndex = this.state.editable.item.length - 1;
        //There is only one child
        if (lastIndex === 0)
            return;
        //First child trying to move up
        if (sectionIndex === 0 && direction === 'up')
            return;
        //Last Child trying to move down
        if (sectionIndex === lastIndex && direction === 'down')
            return;
        const targetIndex = sectionIndex + (direction === 'up' ? -1 : 1);
        const sections = [...this.state.editable.item];
        swapInArrayByIndex(sections, sectionIndex, targetIndex);
        this.state.editable.updateItem(sections);
        this.state.editable.save();
    };
    onSectionDelete = (section) => {
        Dialog_Confirmation.show({
            header: 'Delete Note Template Section',
            content: 'Are you sure you want to delete this section and all of its sub-sections?',
            buttons: [{
                    isAsync: false,
                    isPreferred: false,
                    label: 'Cancel',
                    action: () => ModuleFE_Dialog.close(),
                }, {
                    isAsync: true,
                    isPreferred: true,
                    label: 'Delete',
                    action: async () => section.delete(),
                }]
        });
    };
    addSubSection = (section) => {
        const subSections = section.editProp('subSections', []);
        subSections.updateArrayAt(default_NoteTemplateSection);
    };
    // ######################## Render ########################
    render() {
        const sections = this.state.editable;
        return _jsx(LL_V_L, { className: 'editor__note-template-sections', children: sections.item.map((section, index) => {
                const editable = sections.editProp(index, {});
                editable.setOnDelete(() => sections.removeArrayItem(index));
                return this.renderSection(editable, index);
            }) });
    }
    renderSection = (section, index) => {
        //Using generateHex to get a key because:
        //1. React asserts this returned node must have a key because it's rendered in a .map().
        //2. Index is not viable because section order can be changed.
        return _jsxs(LL_V_L, { className: 'note-template-section', children: [this.render_Section_Editor(section, index), this.render_Section_SubSections(section)] }, generateHex(4));
    };
    // ######################## Render - Section Editor ########################
    render_Section_Editor = (section, index) => {
        return _jsxs(LL_H_C, { className: 'note-template-section__editor', children: [this.render_Section_Editor_Controls(section, index), this.render_Section_Editor_Main(section)] });
    };
    render_Section_Editor_Controls = (section, index) => {
        if (!this.state.editMode)
            return;
        const lastIndex = this.state.editable.item.length - 1;
        const moveUpClassName = _className('move-up', index === 0 && 'disabled');
        const moveDownClassName = _className('move-down', index === lastIndex && 'disabled');
        return _jsxs(LL_V_L, { className: 'note-template-section__editor__controls', children: [_jsx(ICONS.doubleArrow.component, { className: moveUpClassName, onClick: () => this.moveSectionIndex(index, 'up') }), _jsx(ICONS.bin.component, { className: 'delete', onClick: () => this.onSectionDelete(section) }), _jsx(ICONS.addChild.component, { className: 'add-child', onClick: () => this.addSubSection(section) }), _jsx(ICONS.doubleArrow.component, { className: moveDownClassName, onClick: () => this.moveSectionIndex(index, 'down') })] });
    };
    render_Section_Editor_Main = (section) => {
        return _jsxs(LL_V_L, { className: 'note-template-section__editor__main', children: [this.render_Section_Editor_Title(section), this.render_Section_Editor_Flags(section), this.render_Section_Editor_Content(section), this.render_Level(section)] });
    };
    // ######################## Render - Section Main Editors ########################
    render_Level = (section) => {
        return _jsxs("div", { className: 'note-template-section__level', children: ["Level ", this.state.level] });
    };
    render_Section_Editor_Title = (section) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__value', children: section.item.title ?? 'N/A' }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText_Optional, { editable: section, prop: 'title' }) }
                ] }) });
    };
    render_Section_Editor_Content = (section) => {
        const error = section.hasError('content');
        return _jsxs(TS_PropRenderer.Vertical, { label: 'Content', children: [error && _jsxs(LL_H_C, { className: 'editor__note-template-sections__error', children: [_jsx(ICONS.attention.component, {}), error] }), _jsx(DefaultEditor_TextArea, { disabled: this.getViewMode() !== viewMode_Edit, resizeWithText: true, editable: section, prop: 'content' })] });
    };
    render_Section_Editor_Flags = (section) => {
        return _jsxs(LL_H_C, { className: 'note-template-section__editor__main__flags', children: [_jsx(TS_Checkbox, { disabled: !this.state.editMode, checked: section.item.isEditable, onCheck: () => section.updateObj({ isEditable: !section.item.isEditable }), children: "Editable" }), _jsx(TS_Checkbox, { disabled: !this.state.editMode, checked: section.item.isSelectable, onCheck: () => section.updateObj({ isSelectable: !section.item.isSelectable }), children: "Selectable" }), _jsx(TS_Checkbox, { disabled: !this.state.editMode, checked: section.item.isIndented, onCheck: () => section.updateObj({ isIndented: !section.item.isIndented }), children: "Indent" }), _jsx(TS_Checkbox, { disabled: !this.state.editMode, checked: section.item.defaultSelected, onCheck: () => section.updateObj({ defaultSelected: !section.item.defaultSelected }), children: "Default Selected" }), _jsx(TS_Checkbox, { disabled: !this.state.editMode, checked: section.item.dispositionSection, onCheck: () => section.updateObj({ dispositionSection: !section.item.dispositionSection }), children: "Disposition Section" })] });
    };
    // ######################## Render - Sub Sections ########################
    render_Section_SubSections = (section) => {
        if (!section.item.subSections?.length)
            return;
        const editable = section.editProp('subSections', []);
        return _jsx(Editor_NoteTemplateSections, { editable: editable, editMode: this.state.editMode, indent: true, level: this.state.level + 1 });
    };
}
//# sourceMappingURL=Editor_NoteTemplateSections.js.map